"use client"

/*
==================================================
STORE CATEGORY SCROLL MEMORY

Функция:
- запоминает scroll отдельно для каждой категории
- при первом заходе в категорию поднимает страницу вверх
- при возврате в уже просмотренную категорию
  восстанавливает прежний scroll

Родитель:
src/modules/store/templates/index.tsx

Дочерние элементы:
нет

Использует:
sessionStorage

Примечания:
- память работает только в рамках текущей вкладки
- после закрытия вкладки или полной новой сессии сбрасывается
- ключ "__all__" используется для общего списка товаров
==================================================
*/

import { useEffect, useRef } from "react"

const STORAGE_KEY = "store-category-scroll-memory"
const ALL_KEY = "__all__"

type ScrollMap = Record<string, number>

type StoreCategoryScrollMemoryProps = {
  selectedCategory?: string
}

const resolveCategoryKey = (selectedCategory?: string) => {
  return selectedCategory?.trim() ? selectedCategory : ALL_KEY
}

const readScrollMap = (): ScrollMap => {
  if (typeof window === "undefined") {
    return {}
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)

    if (!raw) {
      return {}
    }

    const parsed = JSON.parse(raw)

    return parsed && typeof parsed === "object" ? parsed : {}
  } catch {
    return {}
  }
}

const writeScrollMap = (map: ScrollMap) => {
  if (typeof window === "undefined") {
    return
  }

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    // intentionally empty
  }
}

const saveCategoryScroll = (categoryKey: string, scrollY: number) => {
  const map = readScrollMap()

  map[categoryKey] = Math.max(0, Math.round(scrollY))

  writeScrollMap(map)
}

const restoreCategoryScroll = (categoryKey: string) => {
  const map = readScrollMap()
  const savedY = map[categoryKey]
  const targetY = typeof savedY === "number" ? savedY : 0

  const apply = () => {
    window.scrollTo({
      top: targetY,
      left: 0,
      behavior: "auto",
    })
  }

  apply()
  requestAnimationFrame(apply)
  window.setTimeout(apply, 120)
  window.setTimeout(apply, 260)
}

export default function StoreCategoryScrollMemory({
  selectedCategory,
}: StoreCategoryScrollMemoryProps) {
  const activeCategoryKey = resolveCategoryKey(selectedCategory)
  const previousCategoryKeyRef = useRef(activeCategoryKey)

  useEffect(() => {
    const handleBeforeUnload = () => {
      saveCategoryScroll(activeCategoryKey, window.scrollY)
    }

    window.addEventListener("beforeunload", handleBeforeUnload)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [activeCategoryKey])

  useEffect(() => {
    const previousCategoryKey = previousCategoryKeyRef.current

    saveCategoryScroll(previousCategoryKey, window.scrollY)
    restoreCategoryScroll(activeCategoryKey)

    previousCategoryKeyRef.current = activeCategoryKey
  }, [activeCategoryKey])

  return null
}
